<template>
  <VSearchableTile
    data-testid="drill-data-tile"
    class="drill-data-tile"
    :class="[tileClasses, { 'disabled-error': props.data.hole_data_status !== 'ready' }]"
    :selected="selected"
    :disabled="disabled || isAddedToModel"
    @click="handleClick(data)"
  >
    <template #default>
      <div class="drill-data-tile__label-and-status w-full">
        <p class="drill-data-tile__title truncate">{{ data.name }}</p>
        <p class="drill-data-tile__description">{{ assets }}</p>

        <p v-if="isUpdatedAtMoreRecent(data.created_at, data.updated_at)" class="drill-data-tile__date-uploaded pt-1.5">
          Last updated {{ formatDateTimeMed(data.updated_at) }}
        </p>
        <p v-else class="drill-data-tile__date-uploaded pt-1.5">
          {{ formatDateOnlyMed(data.created_at) }}
        </p>

        <div v-if="isAddedToModel" class="status">
          <CheckCircleIcon class="mr-1 inline-block w-4 h-4" />
          <span>Data set added to model</span>
        </div>
        <div v-else-if="data.hole_data_status !== 'ready'" class="status">
          <span v-if="data.hole_data_status === 'error'" class="text-red-800 text-sm align-middle">
            <ExclamationTriangleIcon class="inline-block mr-1 w-4" /> Data processing failed. Re-upload data in Data Files.
          </span>
          <span v-else class="flex items-center gap-2">
            <AnimatedLoadingSpinner class="w-4" />
            Data processing in progress</span
          >
        </div>
      </div>
      <slot name="access-type" />
    </template>
  </VSearchableTile>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue'
import { computed } from 'vue'
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/vue/24/solid'
import AnimatedLoadingSpinner from '@/components/icons/AnimatedLoadingSpinner.svg'
import { formatDateTimeMed } from '@/lib/datetime-format-utils'

import type { Drill } from '@/entities/drill'
import { formatDateOnlyMed, isUpdatedAtMoreRecent } from '@/lib/datetime-format-utils'

const props = defineProps({
  data: {
    type: Object as PropType<Drill>,
    required: true,
  },
  selected: {
    type: Boolean,
    default: false,
  },
  isAddedToModel: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  type: {
    type: String as PropType<'imports' | 'drills'>,
    default: 'drills',
  },
})

const emit = defineEmits(['selected'])

const assets = computed(() => props.data.assets.map(({ name }) => name).join(', '))
const tileClasses = computed(() => (props.disabled || props.isAddedToModel || props.data.hole_data_status !== 'ready' ? 'disabled' : ''))

function handleClick(data: Drill) {
  if (props.data.hole_data_status === 'ready' && !props.isAddedToModel && !props.disabled) {
    emit('selected', data)
  }
}
</script>

<style lang="stylus" scoped>
.drill-data-tile
  @apply flex justify-between

  &.disabled-error
    @apply cursor-default

    & > *:not(.drill-data-tile__label-and-status, .status), .drill-data-tile__label-and-status > *:not(.status)
      @apply bg-gray-100 opacity-45

  p
    @apply m-0

  &__title
    @apply text-sm leading-5 font-medium text-gray-800

  &__description
    @apply text-xs leading-4 font-normal text-gray-700

  &__date-uploaded
    @apply text-xs leading-4 font-normal text-gray-700

.drill-data-tile.disabled
  @apply cursor-default bg-gray-100

  div.status
    @apply text-xs mt-3
</style>
